import { useState } from 'react'
import { useSearchParams, useNavigate, Link } from 'react-router-dom'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import axios from 'axios'
import { toast } from 'react-hot-toast'
import { useAcceptInviteMutation } from '@/redux/service/userManagement'
import {
  Lock,
  Mail,
  ArrowRight,
  Loader2,
  EyeOff,
  Eye,
  Command,
} from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import logo from '@/assets/logo.png'
import { BASE_URL } from '@/constant'

const ResetPassword = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token')
  const [acceptInvite, { isLoading: isResetting }] = useAcceptInviteMutation()
  const [isSubmittingForgot, setIsSubmittingForgot] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const forgotPasswordSchema = Yup.object().shape({
    email: Yup.string().email('Invalid email').required('Email is required'),
  })

  const resetPasswordSchema = Yup.object().shape({
    password: Yup.string()
      .min(8, 'Password must be at least 8 characters')
      .required('Password is required'),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password')], 'Passwords must match')
      .required('Please confirm your password'),
  })

  const forgotFormik = useFormik({
    initialValues: { email: '' },
    validationSchema: forgotPasswordSchema,
    onSubmit: async values => {
      setIsSubmittingForgot(true)
      try {
        const response = await axios.post(`${BASE_URL}/auth/forgot-password`, {
          email: values.email,
        })
        if (response.data.success) {
          toast.success('Password reset link sent to your email')
        } else {
          toast.error(response.data.message || 'Something went wrong')
        }
      } catch (error: unknown) {
        if (axios.isAxiosError(error)) {
          toast.error(
            error.response?.data?.message ?? 'Failed to send reset link'
          )
        }
      } finally {
        setIsSubmittingForgot(false)
      }
    },
  })

  const resetFormik = useFormik({
    initialValues: { password: '', confirmPassword: '' },
    validationSchema: resetPasswordSchema,
    onSubmit: async values => {
      if (!token) return
      try {
        const result = await acceptInvite({
          token,
          password: values.password,
          type: 2,
        }).unwrap()

        if (result.success) {
          toast.success('Password reset successful')
          navigate('/login')
        }
      } catch (err) {
        const error = err as { data?: { message?: string } }
        toast.error(error?.data?.message ?? 'Failed to reset password')
      }
    },
  })

  return (
    <div className='min-h-screen w-full flex bg-white font-sans selection:bg-black selection:text-white'>
      <div className='hidden lg:flex w-1/2 bg-black flex-col justify-center gap-10 p-12 text-white relative overflow-hidden'>
        <div className='absolute inset-0 opacity-20 pointer-events-none'>
          <div className='absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-white blur-[120px]' />
          <div className='absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-slate-800 blur-[120px]' />
        </div>

        <div className='relative z-10 flex items-center gap-2 group cursor-default'>
          <div className='w-10 h-10 rounded-lg bg-white flex items-center justify-center transition-transform group-hover:scale-110'>
            <img src={logo} alt='logo' className='w-6 h-6 text-black' />
          </div>
          <span className='text-2xl font-bold tracking-tighter'>
            ClickVoltz<span className='text-[#FFC107]'>.</span>
          </span>
        </div>

        <div className='relative z-10 max-w-md'>
          <h1 className='text-5xl font-extrabold tracking-tight leading-[1.1] mb-6'>
            Security <br />
            <span className='text-slate-400'>Handled with Care.</span>
          </h1>
          <p className='text-lg text-slate-400 font-medium'>
            Protect your account and workspace with our secure authentication
            system.
          </p>
        </div>
      </div>

      {/* Form Side */}
      <div className='w-full lg:w-1/2 flex items-center justify-center p-8 bg-slate-50/30'>
        <div className='w-full max-w-[400px] animate-in fade-in slide-in-from-bottom-4 duration-1000'>
          <Card className='border-none shadow-none bg-transparent'>
            <CardHeader className='p-0 mb-8 space-y-2'>
              <div className='lg:hidden flex items-center gap-2 mb-8'>
                <Command className='w-8 h-8 text-black' />
                <span className='text-xl font-bold tracking-tighter'>
                  ClickUp
                </span>
              </div>
              <CardTitle className='text-3xl font-bold tracking-tight text-black'>
                {token ? 'Reset Password' : 'Forgot Password'}
              </CardTitle>
              <CardDescription className='text-slate-500 font-medium'>
                {token
                  ? 'Choose a strong password for your account'
                  : "We'll send a recovery link to your inbox"}
              </CardDescription>
            </CardHeader>
            <CardContent className='p-0'>
              <AnimatePresence mode='wait'>
                {!token ? (
                  <motion.form
                    key='forgot-form'
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    onSubmit={forgotFormik.handleSubmit}
                    className='space-y-6'
                  >
                    <div className='space-y-4'>
                      <div className='space-y-2 group'>
                        <Label
                          htmlFor='email'
                          className='text-sm font-semibold text-slate-700 group-focus-within:text-black transition-colors'
                        >
                          Email Address <span className='text-red-500'>*</span>
                        </Label>
                        <div className='relative'>
                          <Mail className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400' />
                          <Input
                            id='email'
                            type='email'
                            placeholder='name@company.com'
                            className={`pl-10 h-11 bg-white border-slate-200 outline-none focus:ring-2 focus:ring-black focus:border-black transition-all rounded-lg ${
                              forgotFormik.touched.email &&
                              forgotFormik.errors.email
                                ? 'border-red-500 focus:ring-red-500'
                                : ''
                            }`}
                            {...forgotFormik.getFieldProps('email')}
                            disabled={isSubmittingForgot}
                          />
                        </div>
                        {forgotFormik.touched.email &&
                          forgotFormik.errors.email && (
                            <p className='text-xs text-red-600 font-medium ml-1'>
                              {forgotFormik.errors.email}
                            </p>
                          )}
                      </div>
                    </div>

                    <Button
                      type='submit'
                      className='w-full h-11 bg-black hover:bg-slate-800 text-white font-bold rounded-lg transition-all active:scale-[0.98] disabled:opacity-50 disabled:active:scale-100 shadow-xl shadow-black/10'
                      disabled={isSubmittingForgot}
                    >
                      {isSubmittingForgot ? (
                        <Loader2 className='w-5 h-5 animate-spin' />
                      ) : (
                        <span className='flex items-center gap-2'>
                          Send Recovery Link <ArrowRight className='w-4 h-4' />
                        </span>
                      )}
                    </Button>
                  </motion.form>
                ) : (
                  <motion.form
                    key='reset-form'
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    onSubmit={resetFormik.handleSubmit}
                    className='space-y-6'
                  >
                    <div className='space-y-4'>
                      <div className='space-y-2 group'>
                        <Label
                          htmlFor='password'
                          className='text-sm font-semibold text-slate-700 group-focus-within:text-black transition-colors'
                        >
                          New Password <span className='text-red-500'>*</span>
                        </Label>
                        <div className='relative'>
                          <Lock className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400' />
                          <Input
                            id='password'
                            type={showPassword ? 'text' : 'password'}
                            placeholder='••••••••'
                            className={`pl-10 pr-10 h-11 bg-white text-black border-slate-200 outline-none focus:ring-2 focus:ring-black focus:border-black transition-all rounded-lg ${
                              resetFormik.touched.password &&
                              resetFormik.errors.password
                                ? 'border-red-500 focus:ring-red-500'
                                : ''
                            }`}
                            {...resetFormik.getFieldProps('password')}
                            disabled={isResetting}
                          />
                          <button
                            type='button'
                            onClick={() => setShowPassword(prev => !prev)}
                            className='absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-black transition-colors'
                            tabIndex={-1}
                          >
                            {showPassword ? (
                              <Eye className='w-4 h-4' />
                            ) : (
                              <EyeOff className='w-4 h-4' />
                            )}
                          </button>
                        </div>
                        {resetFormik.touched.password &&
                          resetFormik.errors.password && (
                            <p className='text-xs text-red-600 font-medium ml-1'>
                              {resetFormik.errors.password}
                            </p>
                          )}
                      </div>

                      <div className='space-y-2 group'>
                        <Label
                          htmlFor='confirmPassword'
                          className='text-sm font-semibold text-slate-700 group-focus-within:text-black transition-colors'
                        >
                          Confirm Password{' '}
                          <span className='text-red-500'>*</span>
                        </Label>
                        <div className='relative'>
                          <Lock className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400' />
                          <Input
                            id='confirmPassword'
                            type={showConfirmPassword ? 'text' : 'password'}
                            placeholder='••••••••'
                            className={`pl-10 pr-10 h-11 bg-white text-black border-slate-200 outline-none focus:ring-2 focus:ring-black focus:border-black transition-all rounded-lg ${
                              resetFormik.touched.confirmPassword &&
                              resetFormik.errors.confirmPassword
                                ? 'border-red-500 focus:ring-red-500'
                                : ''
                            }`}
                            {...resetFormik.getFieldProps('confirmPassword')}
                            disabled={isResetting}
                          />
                          <button
                            type='button'
                            onClick={() =>
                              setShowConfirmPassword(prev => !prev)
                            }
                            className='absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-black transition-colors'
                            tabIndex={-1}
                          >
                            {showConfirmPassword ? (
                              <Eye className='w-4 h-4' />
                            ) : (
                              <EyeOff className='w-4 h-4' />
                            )}
                          </button>
                        </div>
                        {resetFormik.touched.confirmPassword &&
                          resetFormik.errors.confirmPassword && (
                            <p className='text-xs text-red-600 font-medium ml-1'>
                              {resetFormik.errors.confirmPassword}
                            </p>
                          )}
                      </div>
                    </div>

                    <Button
                      type='submit'
                      className='w-full h-11 bg-black hover:bg-slate-800 text-white font-bold rounded-lg transition-all active:scale-[0.98] disabled:opacity-50 disabled:active:scale-100 shadow-xl shadow-black/10'
                      disabled={isResetting}
                    >
                      {isResetting ? (
                        <Loader2 className='w-5 h-5 animate-spin' />
                      ) : (
                        <span className='flex items-center gap-2'>
                          Reset Password
                        </span>
                      )}
                    </Button>
                  </motion.form>
                )}
              </AnimatePresence>

              <div className='mt-8 border-slate-100 flex justify-center'>
                <Link
                  to='/login'
                  className='text-sm text-slate-500 hover:text-black transition-colors font-medium flex items-center gap-2'
                >
                  Back to Login
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}

export default ResetPassword
